// src/screens/RoleRevealScreen.jsx
import { useState, useEffect } from "react";
import { Btn } from "../components/ui.jsx";

const ROLE_DATA = {
  mafia: {
    icon: "🔪", name: "MAFIA",
    color: { background: "linear-gradient(135deg,#1a0505,#3d0f0f)", border: "2px solid var(--blood)" },
    nameColor: "#e05555",
    desc: "Eliminate town members each night. Stay hidden. Win when Mafia ≥ Town.",
  },
  doctor: {
    icon: "💊", name: "DOCTOR",
    color: { background: "linear-gradient(135deg,#051515,#0f3030)", border: "2px solid #2a8080" },
    nameColor: "#4db8b8",
    desc: "Save one player each night — even yourself. The town depends on you.",
  },
  citizen: {
    icon: "🏛️", name: "CITIZEN",
    color: { background: "linear-gradient(135deg,#050a1a,#0f1e40)", border: "2px solid #2a4080" },
    nameColor: "#6080c0",
    desc: "Find the Mafia through deduction and town votes. Trust no one.",
  },
};

export default function RoleRevealScreen({ state, me, onReady, onBeginRound }) {
  const player = (state.players || []).find((p) => p.id === me.id);
  const role   = player?.role || "citizen";
  const rd     = ROLE_DATA[role] || ROLE_DATA.citizen;

  const ready    = state.revealReady || [];
  const total    = (state.players || []).length;
  const allReady = ready.length >= total && total > 0;
  const iAmReady = ready.includes(me.id);

  // Allies (same-role teammates)
  const allies = (role === "mafia" || role === "doctor")
    ? (state.players || []).filter((p) => p.id !== me.id && p.role === role).map((p) => p.name)
    : [];

  // Card flip state: 'visible' | 'flipping' | 'hidden'
  const [cardState, setCardState] = useState("visible");

  // Auto-flip card after player marks ready
  useEffect(() => {
    if (iAmReady && cardState === "visible") {
      // short delay so the state update is visible, then flip out
      const t = setTimeout(() => setCardState("flipping"), 600);
      return () => clearTimeout(t);
    }
  }, [iAmReady]);

  useEffect(() => {
    if (cardState === "flipping") {
      const t = setTimeout(() => setCardState("hidden"), 450);
      return () => clearTimeout(t);
    }
  }, [cardState]);

  // Auto-proceed: when ALL players ready, host advances after 2s
  useEffect(() => {
    if (allReady && me.isHost) {
      const t = setTimeout(() => onBeginRound(), 2200);
      return () => clearTimeout(t);
    }
  }, [allReady, me.isHost]);

  return (
    <div style={{ minHeight: "100vh", padding: "2rem 1rem", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", animation: "fadeInUp 0.5s ease" }}>
      <div style={{ textAlign: "center", marginBottom: "1rem", color: "rgba(242,234,216,0.45)", fontSize: "0.7rem", letterSpacing: "0.22em" }}>
        YOUR ROLE
      </div>

      {/* ── Role Card ── */}
      <div style={{ perspective: "800px", marginBottom: "2rem" }}>
        {cardState !== "hidden" ? (
          <div
            className={cardState === "flipping" ? "role-flip-exit" : "role-flip-enter"}
            style={{
              width: "clamp(270px, 80vw, 320px)",
              padding: "clamp(1.5rem, 5vw, 3rem) clamp(1rem, 4vw, 2rem)",
              textAlign: "center",
              borderRadius: 4,
              ...rd.color,
            }}
          >
            <span style={{ fontSize: "clamp(2.5rem, 8vw, 4rem)", marginBottom: "0.8rem", display: "block" }}>
              {rd.icon}
            </span>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.5rem, 5vw, 2rem)", fontWeight: 900, letterSpacing: "0.1em", marginBottom: "0.5rem", color: rd.nameColor }}>
              {rd.name}
            </div>
            <div style={{ fontSize: "clamp(0.72rem, 2vw, 0.82rem)", color: "rgba(242,234,216,0.6)", lineHeight: 1.65, marginBottom: allies.length ? "1.5rem" : 0 }}>
              {rd.desc}
            </div>
            {allies.length > 0 && (
              <div style={{ fontSize: "0.7rem", color: "rgba(242,234,216,0.4)", letterSpacing: "0.1em", borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "1rem" }}>
                {role === "mafia" ? "Fellow Mafia" : "Fellow Doctors"}:{" "}
                <strong style={{ color: rd.nameColor }}>{allies.join(", ")}</strong>
              </div>
            )}
          </div>
        ) : (
          /* Card back — shown after flip */
          <div style={{ width: "clamp(270px, 80vw, 320px)", padding: "3rem 2rem", textAlign: "center", borderRadius: 4, background: "linear-gradient(135deg,#111,#1a1a1a)", border: "2px solid rgba(201,168,76,0.2)", color: "rgba(201,168,76,0.4)" }}>
            <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>🃏</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1rem", letterSpacing: "0.2em" }}>
              ROLE HIDDEN
            </div>
          </div>
        )}
      </div>

      {/* ── Status ── */}
      <div style={{ textAlign: "center", maxWidth: 420, width: "100%" }}>
        <p style={{ color: "rgba(242,234,216,0.4)", fontSize: "0.72rem", letterSpacing: "0.1em", marginBottom: "1.25rem" }}>
          Memorise your role. Do <strong style={{ color: "var(--paper)" }}>not</strong> reveal it to anyone.
        </p>

        {!iAmReady ? (
          <Btn
            onClick={onReady}
            style={{ maxWidth: 320, margin: "0 auto 1.25rem", display: "block" }}
          >
            ✓ I've seen my role — I'm ready
          </Btn>
        ) : (
          <div style={{ color: "var(--gold-dim)", fontSize: "0.78rem", marginBottom: "1.25rem", letterSpacing: "0.1em" }}>
            ✓ Locked in. Waiting for others...
          </div>
        )}

        {/* Live counter */}
        <div style={{
          color: "var(--gold-dim)", fontSize: "0.78rem", marginBottom: "1rem",
          animation: allReady ? "none" : "pulse 1.6s ease-in-out infinite",
        }}>
          {allReady
            ? (me.isHost ? "All ready! Starting game..." : "All ready! Host is starting...")
            : `${ready.length} / ${total} players confirmed`}
        </div>

        {/* Progress bar */}
        <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden", maxWidth: 300, margin: "0 auto" }}>
          <div style={{ height: "100%", width: `${(ready.length / Math.max(total, 1)) * 100}%`, background: "var(--gold)", transition: "width 0.4s ease", borderRadius: 2 }} />
        </div>
      </div>
    </div>
  );
}
